import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import DictationInput from '../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Trash2, CheckCircle, XCircle, Briefcase, GraduationCap, 
  Heart, Home, Users, MessageSquare, Award, RefreshCw, Send, Building
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { storeReflection } from '../lib/aiReflections';

const NEED_CATEGORIES = [
  { value: 'documents', label: 'ID & Documents', icon: Award },
  { value: 'employment', label: 'Employment', icon: Briefcase },
  { value: 'education', label: 'Education/Training', icon: GraduationCap },
  { value: 'housing', label: 'Housing', icon: Home },
  { value: 'transportation', label: 'Transportation', icon: Building },
  { value: 'healthcare', label: 'Healthcare', icon: Heart },
  { value: 'clothing', label: 'Clothing', icon: Users },
  { value: 'general', label: 'General Support', icon: Users },
];

const NEED_SUGGESTIONS = [
  { title: 'Social Security card', category: 'documents', description: 'Apply for or replace Social Security card.' },
  { title: 'State ID / Driver\'s License', category: 'documents', description: 'Obtain or renew ID/Driver\'s License.' },
  { title: 'Housing placement', category: 'housing', description: 'Secure stable housing or transitional living.' },
  { title: 'Transportation support', category: 'transportation', description: 'Bus pass, ride access, or reliable transportation.' },
  { title: 'Employment readiness', category: 'employment', description: 'Job search support, interviews, or onboarding.' },
  { title: 'Clothing essentials', category: 'clothing', description: 'Professional or daily clothing needs.' },
  { title: 'Dental appointment', category: 'healthcare', description: 'Schedule dental care or checkup.' },
  { title: 'Medical necessities', category: 'healthcare', description: 'Primary care, medication, or medical access.' }
];

export default function MyNeedsPage() {
  const { user, axiosAuth } = useAuth();
  const canEditSkills = ['coordinator', 'admin'].includes(user.role);
  const [profile, setProfile] = useState(null);
  const [myNeeds, setMyNeeds] = useState([]);
  const [connectionOffers, setConnectionOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseOriginal, setResponseOriginal] = useState('');
  const [newNeed, setNewNeed] = useState({ title: '', description: '', category: 'general' });
  const [newNeedOriginal, setNewNeedOriginal] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  const [aboutMeOriginal, setAboutMeOriginal] = useState('');
  const api = axiosAuth();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, needsRes, offersRes] = await Promise.all([
        api.get('/participant-profile/me'),
        api.get('/participant-profile/needs/mine'),
        api.get('/connections/my')
      ]);
      setProfile(profileRes.data);
      setMyNeeds(needsRes.data || []);
      setConnectionOffers(offersRes.data || []);
    } catch (err) {
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (profile?.bio_summary) {
      setAboutMe(profile.bio_summary);
      setAboutMeOriginal(profile.bio_summary);
    }
  }, [profile]);

  const handleAddNeed = async () => {
    if (!newNeed.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    try {
      await api.post(`/participant-profile/needs?title=${encodeURIComponent(newNeed.title)}&description=${encodeURIComponent(newNeed.description)}&category=${newNeed.category}`);
      toast.success('Need added! Community partners can now see it.');
      if (newNeed.description?.trim()) {
        await storeReflection(api, {
          original_text: newNeedOriginal || newNeed.description,
          refined_text: newNeed.description,
          context: 'needs',
          task_name: newNeed.title,
          task_description: 'Need description submitted to community partners',
          category: newNeed.category,
          source: 'my_needs'
        });
      }
      setAddDialogOpen(false);
      setNewNeed({ title: '', description: '', category: 'general' });
      setNewNeedOriginal('');
      fetchData();
    } catch (err) {
      toast.error('Failed to add need');
    }
  };

  const handleQuickAdd = async (suggestion) => {
    try {
      await api.post(`/participant-profile/needs?title=${encodeURIComponent(suggestion.title)}&description=${encodeURIComponent(suggestion.description)}&category=${suggestion.category}`);
      toast.success('Need added');
      fetchData();
    } catch {
      toast.error('Unable to add need');
    }
  };

  const handleDeleteNeed = async (needId) => {
    try {
      await api.delete(`/participant-profile/needs/${needId}`);
      toast.success('Need removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove need');
    }
  };

  const handleRespond = async (action) => {
    if (!selectedOffer) return;
    try {
      await api.put(`/connections/${selectedOffer.id}/respond?action=${action}&message=${encodeURIComponent(responseMessage)}`);
      toast.success(action === 'accepted' ? 'Connection accepted!' : 'Connection declined');
      if (responseMessage?.trim()) {
        await storeReflection(api, {
          original_text: responseOriginal || responseMessage,
          refined_text: responseMessage,
          context: 'connection_response',
          task_name: 'Connection response',
          task_description: 'Response sent to community partner',
          category: selectedOffer?.category || 'general',
          source: 'my_needs'
        });
      }
      setRespondDialogOpen(false);
      setSelectedOffer(null);
      setResponseMessage('');
      setResponseOriginal('');
      fetchData();
    } catch (err) {
      toast.error('Failed to respond');
    }
  };

  const openRespondDialog = (offer) => {
    setSelectedOffer(offer);
    setRespondDialogOpen(true);
  };

  const updateProfile = async (field, value) => {
    try {
      await api.put(`/participant-profile/me?${field}=${encodeURIComponent(value)}`);
      toast.success('Profile updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  const handleSaveAboutMe = async () => {
    if (!aboutMe.trim()) return toast.error('Please add a short summary');
    await updateProfile('bio_summary', aboutMe);
    await storeReflection(api, {
      original_text: aboutMeOriginal || aboutMe,
      refined_text: aboutMe,
      context: 'bio_summary',
      task_name: 'About Me',
      task_description: 'Public summary for partners',
      category: 'identity',
      source: 'my_needs'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-needs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            My Skills & Needs
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Share what you're looking for and showcase your skills
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2">
          <Plus size={16} /> Add a Need
        </Button>
      </div>

      <Tabs defaultValue="needs" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-full p-1">
          <TabsTrigger value="needs" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            My Needs ({myNeeds.length})
          </TabsTrigger>
          <TabsTrigger value="offers" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            Connection Offers ({connectionOffers.length})
          </TabsTrigger>
          <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4">
            My Profile
          </TabsTrigger>
        </TabsList>

        {/* My Needs Tab */}
        <TabsContent value="needs" className="space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl" data-testid="needs-suggestions">
            <CardContent className="p-4">
              <p className="text-sm font-semibold mb-2">Quick-add common needs</p>
              <div className="flex flex-wrap gap-2">
                {NEED_SUGGESTIONS.map(suggestion => (
                  <Button
                    key={suggestion.title}
                    variant="outline"
                    className="rounded-full text-xs"
                    onClick={() => handleQuickAdd(suggestion)}
                    data-testid={`need-suggestion-${suggestion.title.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {suggestion.title}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
          {myNeeds.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">You haven't added any needs yet.</p>
                <p className="text-sm text-muted-foreground mt-1">Add needs to let community partners know how they can help!</p>
                <Button onClick={() => setAddDialogOpen(true)} className="mt-4 bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2">
                  <Plus size={16} /> Add Your First Need
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {myNeeds.map(need => {
                const catInfo = NEED_CATEGORIES.find(c => c.value === need.category) || NEED_CATEGORIES[NEED_CATEGORIES.length - 1];
                const CatIcon = catInfo.icon;
                return (
                  <Card key={need.id} className="border-0 shadow-sm rounded-2xl">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                            <CatIcon className="text-teal-600" size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{need.title}</h3>
                            <Badge variant="outline" className="text-xs mt-0.5">{catInfo.label}</Badge>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteNeed(need.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      {need.description && (
                        <p className="text-sm text-muted-foreground mt-3">{need.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-3">Added {formatDate(need.created_at)}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Connection Offers Tab */}
        <TabsContent value="offers" className="space-y-4">
          {connectionOffers.length === 0 ? (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No connection offers yet.</p>
                <p className="text-sm text-muted-foreground mt-1">When community partners want to help, their offers will appear here.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {connectionOffers.map(offer => (
                <Card key={offer.id} className="border-0 shadow-sm rounded-2xl border-l-4 border-l-emerald-400">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                            <Building className="text-emerald-600" size={20} />
                          </div>
                          <div>
                            <p className="font-semibold">{offer.partner_name}</p>
                            <p className="text-xs text-muted-foreground">Community Partner</p>
                          </div>
                        </div>
                        <div className="bg-muted/30 p-3 rounded-xl">
                          <p className="text-sm font-medium mb-1">Regarding: {offer.need_title}</p>
                          {offer.message && <p className="text-sm text-muted-foreground">{offer.message}</p>}
                          {offer.resource_offered && (
                            <p className="text-sm mt-2">
                              <span className="font-medium">They're offering:</span> {offer.resource_offered}
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-3">Received {formatDate(offer.updated_at)}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => openRespondDialog(offer)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-1"
                        >
                          <CheckCircle size={14} /> Accept
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setSelectedOffer(offer); handleRespond('declined'); }}
                          className="rounded-full gap-1 border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <XCircle size={14} /> Decline
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-4">
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Award className="text-teal-600" size={18} /> My Skills & Experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                {canEditSkills
                  ? 'Add skills based on screening assessment. Separate with commas.'
                  : 'Skills are added by your program coordinator based on your assessment.'}
              </p>
              <div className="flex gap-2">
                <Input 
                  placeholder="e.g., Carpentry, Customer Service, Forklift Certified"
                  defaultValue={profile?.skills?.join(', ') || ''}
                  onBlur={(e) => canEditSkills && updateProfile('skills', e.target.value)}
                  className="rounded-xl"
                  disabled={!canEditSkills}
                  data-testid="skills-input"
                />
              </div>
              {profile?.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {profile.skills.map((skill, idx) => (
                    <Badge key={`item-${idx}`} className="bg-teal-50 text-teal-700">{skill}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <GraduationCap className="text-sky-600" size={18} /> My Accomplishments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Share certifications, training, or achievements. Separate with commas.
              </p>
              <div className="flex gap-2">
                <Input 
                  placeholder="e.g., GED, OSHA 10, Food Handler Certificate"
                  defaultValue={profile?.accomplishments?.join(', ') || ''}
                  onBlur={(e) => updateProfile('accomplishments', e.target.value)}
                  className="rounded-xl"
                />
              </div>
              {profile?.accomplishments?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-3">
                  {profile.accomplishments.map((acc, idx) => (
                    <Badge key={`item-${idx}`} className="bg-sky-50 text-sky-700">{acc}</Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Users className="text-violet-600" size={18} /> About Me
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Write a brief summary about yourself that partners can see.
              </p>
              <DictationInput 
                value={aboutMe}
                onChange={(cleaned, original) => { setAboutMe(cleaned); setAboutMeOriginal(original || cleaned); }}
                placeholder="Tell us a bit about yourself, your goals, and what kind of opportunities you're looking for..."
                className="rounded-xl"
                rows={4}
                promptContext={{
                  context: 'bio_summary',
                  taskName: 'About Me',
                  taskDescription: 'Short public summary for partners',
                  category: 'identity'
                }}
                data-testid="about-me-input"
              />
              <Button onClick={handleSaveAboutMe} className="mt-3 bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="save-about-me">
                Save About Me
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Need Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Add a Need</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">What do you need help with?</label>
              <Input 
                value={newNeed.title}
                onChange={(e) => setNewNeed({ ...newNeed, title: e.target.value })}
                placeholder="e.g., Looking for warehouse work, Need help with resume"
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Category</label>
              <Select value={newNeed.category} onValueChange={(v) => setNewNeed({ ...newNeed, category: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NEED_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Details (optional)</label>
              <DictationInput 
                value={newNeed.description}
                onChange={(cleaned, original) => { setNewNeed({ ...newNeed, description: cleaned }); setNewNeedOriginal(original || cleaned); }}
                placeholder="Add more details about what you're looking for..."
                className="rounded-xl"
                rows={3}
                promptContext={{
                  context: 'needs',
                  taskName: newNeed.title || 'New Need',
                  taskDescription: 'Describe the support you want',
                  category: newNeed.category
                }}
                data-testid="need-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={handleAddNeed} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2">
              <Plus size={16} /> Add Need
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond Dialog */}
      <Dialog open={respondDialogOpen} onOpenChange={setRespondDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Accept Connection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedOffer && (
              <div className="bg-muted/30 p-3 rounded-xl">
                <p className="font-medium">{selectedOffer.partner_name}</p>
                <p className="text-sm text-muted-foreground mt-1">{selectedOffer.resource_offered}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Your Response (optional)</label>
              <DictationInput 
                value={responseMessage}
                onChange={(cleaned, original) => { setResponseMessage(cleaned); setResponseOriginal(original || cleaned); }}
                placeholder="Thank you for reaching out! I'm interested in..."
                className="rounded-xl"
                rows={3}
                promptContext={{
                  context: 'connection_response',
                  taskName: 'Connection response',
                  taskDescription: 'Reply to partner outreach',
                  category: selectedOffer?.category || 'general'
                }}
                data-testid="connection-response"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondDialogOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={() => handleRespond('accepted')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full gap-2">
              <Send size={16} /> Accept & Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
